import React, { useMemo, useState } from "react"
import { Row, Col, Button, Typography, DatePicker } from "antd"
import { FaFilePdf } from "react-icons/fa"
import moment from "moment"
import {
  PDFDownloadLink,
  Page as PdfPage,
  Text as PdfText,
  View as PdfView,
  Document as PdfDocument,
  StyleSheet as PdfStyleSheet,
} from "@react-pdf/renderer"

const { Text } = Typography
const { RangePicker } = DatePicker

// ---------- PDF styles ----------
const pdfStyles = PdfStyleSheet.create({
  page: { padding: 24, fontSize: 10 },
  h1: { fontSize: 16, marginBottom: 8 },
  meta: { fontSize: 9, marginBottom: 8 },
  faint: { color: "#555" },
  groupHeader: { fontSize: 11, marginTop: 10, marginBottom: 4 },

  table: { width: "100%", borderWidth: 1, borderColor: "#000" },
  row: { flexDirection: "row" },

  // header with green bg
  headerCell: {
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    fontSize: 9,
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#28a745",
  },

  // regular cell
  cell: {
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    fontSize: 9,
  },

  // remove right border on last col
  cellLast: { borderRightWidth: 0 },

  // column sizing
  colBarcode: { flex: 3 },
  colWidth: { flex: 1 },
  colMeters: { flex: 1.5 },
  colPrice: { flex: 1.5 },
  colDate: { flex: 1.5 },
  colOrder: { flex: 1 },

  totals: { marginTop: 6, marginBottom: 10, fontSize: 10, color: "#28a745" },
  grandTotals: {
    marginTop: 12,
    paddingTop: 6,
    borderTopWidth: 1,
    fontSize: 11,
  },
})

// ---------- helpers ----------
const fmtMoney = (n) =>
  typeof n === "number"
    ? n
        .toLocaleString("lv-LV", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .replace(/\u00A0/g, " ")
    : "-"

const fmtInt = (n) =>
  typeof n === "number" ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "-"

const fmtDate = (ts) => (ts && ts.seconds ? moment.unix(ts.seconds).format("DD/MM/YYYY") : "-")

function groupByMaterial(rolls) {
  const map = new Map()
  rolls.forEach((r) => {
    const key = r?.material?.material || "Unknown material"
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(r)
  })
  return Array.from(map.entries()).map(([material, items]) => ({ material, items }))
}

// ---------- PDF document ----------
function SoldRollsPDF({ rolls, dateFromMs, dateToMs }) {
  const groups = groupByMaterial(rolls)
  const grand = rolls.reduce(
    (acc, r) => {
      acc.meters += r?.originalMeters || 0
      acc.price += r?.price || 0
      return acc
    },
    { meters: 0, price: 0 }
  )

  return (
    <PdfDocument>
      <PdfPage size="A4" style={pdfStyles.page} wrap>
        <PdfText style={pdfStyles.h1}>Sold Rolls Report</PdfText>
        <PdfText style={pdfStyles.meta}>
          Period: {dateFromMs ? moment(dateFromMs).format("DD/MM/YYYY") : "—"} —{" "}
          {dateToMs ? moment(dateToMs).format("DD/MM/YYYY") : "—"}
        </PdfText>
        <PdfText style={[pdfStyles.meta, pdfStyles.faint]}>
          Generated: {moment().format("DD/MM/YYYY HH:mm")}
        </PdfText>

        {groups.map((g, gi) => {
          const totals = g.items.reduce(
            (acc, r) => {
              acc.meters += r?.originalMeters || 0
              acc.price += r?.price || 0
              return acc
            },
            { meters: 0, price: 0 }
          )

          return (
            <PdfView key={gi} wrap={false}>
              <PdfText style={pdfStyles.groupHeader}>{g.material}</PdfText>

              {/* header */}
              <PdfView style={[pdfStyles.table, { borderBottomWidth: 0 }]}>
                <PdfView style={pdfStyles.row}>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colBarcode]}>Barcode</PdfText>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colWidth]}>Width (mm)</PdfText>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colMeters]}>
                    Original Meters
                  </PdfText>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colPrice]}>Price (€)</PdfText>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colDate]}>Shipped At</PdfText>
                  <PdfText style={[pdfStyles.headerCell, pdfStyles.colOrder, pdfStyles.cellLast]}>
                    Order
                  </PdfText>
                </PdfView>

                {g.items.map((r, idx) => (
                  <PdfView style={pdfStyles.row} key={idx}>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colBarcode]}>
                      {r?.barcode || "-"}
                    </PdfText>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colWidth]}>
                      {fmtInt(r?.rollWidth)}
                    </PdfText>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colMeters]}>
                      {fmtInt(r?.originalMeters)}
                    </PdfText>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colPrice]}>
                      {fmtMoney(r?.price)}
                    </PdfText>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colDate]}>
                      {fmtDate(r?.shippedAt)}
                    </PdfText>
                    <PdfText style={[pdfStyles.cell, pdfStyles.colOrder, pdfStyles.cellLast]}>
                      {r?.fullOrderNumber || "-"}
                    </PdfText>
                  </PdfView>
                ))}
              </PdfView>

              <PdfText style={pdfStyles.totals}>
                Group totals — Meters: {fmtInt(totals.meters)} | Sum: € {fmtMoney(totals.price)}
              </PdfText>
            </PdfView>
          )
        })}

        <PdfText style={pdfStyles.grandTotals}>
          Grand totals — Meters: {fmtInt(grand.meters)} | Sum: € {fmtMoney(grand.price)}
        </PdfText>
      </PdfPage>
    </PdfDocument>
  )
}

// ---------- main export component ----------
export default function SoldRollsExport({
  data = [],
  loading = false,
  // Controlled mode (optional):
  pickerRange, // [dayjs, dayjs] | null
  rangeMs, // [startMs, endMs]
  onChangeRange, // function(dayjsRange)
  // Optional sold predicate override:
  isSold: isSoldOverride,
}) {
  const isControlled =
    pickerRange !== undefined && rangeMs !== undefined && typeof onChangeRange === "function"

  // Fallback (uncontrolled) state if not controlled from parent
  const [localPickerRange, setLocalPickerRange] = useState(null)
  const [localRangeMs, setLocalRangeMs] = useState([null, null])

  const pr = isControlled ? pickerRange : localPickerRange
  const rms = isControlled ? rangeMs : localRangeMs

  const handleRangeChange = (v) => {
    if (isControlled) {
      onChangeRange(v)
    } else {
      setLocalPickerRange(v || null)
      if (v && v[0] && v[1]) {
        const start = v[0].startOf("day").valueOf()
        const end = v[1].endOf("day").valueOf()
        setLocalRangeMs([start, end])
      } else {
        setLocalRangeMs([null, null])
      }
    }
  }

  const defaultIsSold = (r) => r && r.shipping === true && r.shippedAt && r.shippedAt.seconds
  const isSold = isSoldOverride || defaultIsSold

  const filteredSoldRolls = useMemo(() => {
    let rows = (data || []).filter(isSold)
    const [startMs, endMs] = rms || []
    if (startMs && endMs) {
      rows = rows.filter((r) => {
        const t = r?.shippedAt?.seconds ? r.shippedAt.seconds * 1000 : null
        return t && t >= startMs && t <= endMs
      })
    }
    return rows
  }, [data, rms, isSold])

  return (
    <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 8 }}>
      <Col flex="none">
        <Text strong>Sold date:</Text>
      </Col>
      <Col flex="none">
        <RangePicker allowClear value={pr} onChange={handleRangeChange} />
      </Col>
      {/* <Col flex="none">
        <PDFDownloadLink
          document={
            <SoldRollsPDF rolls={filteredSoldRolls} dateFromMs={rms?.[0]} dateToMs={rms?.[1]} />
          }
          fileName={`sold-rolls_${rms?.[0] ? moment(rms[0]).format("YYYYMMDD") : "all"}_${
            rms?.[1] ? moment(rms[1]).format("YYYYMMDD") : "all"
          }.pdf`}>
          {({ loading: pdfLoading }) => (
            <Button
              type="primary"
              icon={<FaFilePdf />}
              disabled={loading || !filteredSoldRolls.length || pdfLoading}>
              {pdfLoading ? "Preparing PDF..." : `Export PDF (${filteredSoldRolls.length} Rolls)`}
            </Button>
          )}
        </PDFDownloadLink>
      </Col> */}
    </Row>
  )
}
